import { useEffect, useState } from 'react';
import type { WeatherAlert } from '@greeniq/shared';
import { acknowledgeWeatherAlert, getWeatherAlerts } from '../api/weather.js';
import { DataCard } from './ui/data-card';
import { Badge, type BadgeProps } from './ui/badge';
import { AlertRow } from './AlertRow';

interface Props {
  courseSlug: string;
}

const TITLE: Record<string, string> = {
  frost_warning: 'Frost expected',
  hard_freeze: 'Hard freeze expected',
  spray_window_closing: 'Spray window closing',
};

const SEVERITY_VARIANT: Record<string, BadgeProps['variant']> = {
  frost_warning: 'warning',
  hard_freeze: 'destructive',
  spray_window_closing: 'default',
};

export function WeatherAlertsInbox({ courseSlug }: Props) {
  const [alerts, setAlerts] = useState<WeatherAlert[] | null>(null);
  const [acking, setAcking] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const resp = await getWeatherAlerts(courseSlug, 'unacknowledged');
      setAlerts(resp.alerts);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [courseSlug]);

  async function onAck(alertId: number) {
    setAcking(alertId);
    try {
      await acknowledgeWeatherAlert(courseSlug, alertId);
      setAlerts((prev) => (prev ?? []).filter((a) => a.id !== alertId));
    } finally {
      setAcking(null);
    }
  }

  if (loading) return null;
  if (!alerts || alerts.length === 0) return null;

  return (
    <DataCard
      className="mt-6"
      title="Weather alerts"
      actions={<Badge variant="destructive">{alerts.length} unacknowledged</Badge>}
    >
      <ul>
        {alerts.map((a) => {
          const title = TITLE[a.alertType] ?? a.alertType;
          const variant = SEVERITY_VARIANT[a.alertType] ?? 'warning';
          const tempMin =
            a.detail && typeof a.detail.tempMinF === 'number' ? (a.detail.tempMinF as number) : null;
          return (
            <AlertRow
              key={a.id}
              badge={a.severity}
              badgeVariant={variant}
              title={title}
              meta={
                <>
                  {a.forDate}
                  {tempMin !== null && ` · expected low ${tempMin.toFixed(0)}°F`}
                </>
              }
              onAcknowledge={() => onAck(a.id)}
              acknowledging={acking === a.id}
            />
          );
        })}
      </ul>
    </DataCard>
  );
}
